/**
 * Rozliczenia (T-C2) — the credits ledger: the clients' balances on the left, the open client's
 * history and its four acts on the right (`useLedgerTab`, `LedgerCard`).
 *
 * The header keeps its one row: the canon's „Rozliczam żetonami" card cannot stand in it, so the
 * switch and its words go into the row's right slot — where the calendar's `+` and Grafik's zone
 * stand — and the card's sentence becomes the switch's description (it is said out loud where it
 * matters most, on the off state, T-C8). Switching off asks first (T-B1b); on is sent at once.
 * The canon's subtitle is the quiet line over the tab.
 *
 * One gate over the summary, the roster, the hours and the first open client's history: nothing is
 * drawn until all four have answered, and then both cards at once, at the height they keep. With
 * credits off the tab says so and offers the switch (T-C8).
 */

import { createPortal } from 'react-dom';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import ember from '~/shared/ui/emberButton.module.css';
import { creditActClass } from '~/features/credit-ledger';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { EmptyState } from '~/shared/ui/EmptyState';
import { CreditMarkIcon, InboxMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import { Toggle } from '~/shared/ui/Toggle';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useLedgerTab } from '../lib/useLedgerTab';
import { proseDate } from '../model/calendarWords';
import type { ClientBalanceRow } from '../model/creditLedger';
import { BalancesCard } from './BalancesCard';
import { LedgerCard } from './LedgerCard';
import frame from './bookingTab.module.css';
import styles from './LedgerTab.module.css';

interface LedgerTabProps {
  /** The header row's right end, where the switch stands while this tab is shown. */
  readonly headerActions: HTMLElement | null;
  /** The client the address opens on — the client card's „Otwórz rozliczenie". */
  readonly client: number | null;
  readonly onOpenClient: (customerId: number) => void;
}

export function LedgerTab({ headerActions, client, onOpenClient }: LedgerTabProps) {
  const { t, i18n } = useTranslation();
  const tab = useLedgerTab(client);
  const effectId = useId();
  const { gate, content } = tab;
  const nameOf = (row: ClientBalanceRow) =>
    row.name ?? t('web:booking.trainer.credits.eyebrowFallback');
  const context = { zone: tab.zone, today: tab.today, nowMs: tab.nowMs };

  return (
    <div className={frame.scroller}>
      {headerActions === null || tab.creditsOn === null
        ? null
        : createPortal(
            <span className={styles.switch} aria-busy={tab.switching}>
              <span className={styles.switchLabel} aria-hidden="true">
                {t('web:booking.credits.switchLabel')}
              </span>
              <span id={effectId} className={hidden.srOnly}>
                {t('web:booking.trainer.settings.rows.creditsEnabled.effect')}
              </span>
              <Toggle
                checked={tab.creditsOn}
                label={t('web:booking.credits.switchLabel')}
                writesAtOnce
                describedBy={effectId}
                onChange={tab.switchTo}
              />
            </span>,
            headerActions,
          )}
      <p className={frame.intro}>{t('web:booking.credits.intro')}</p>
      {tab.switchRefusal === null ? null : (
        <p className={styles.refusal} role="alert">
          {tab.switchRefusal}
        </p>
      )}

      {/* The two cards' hand-over rides on the row, which stands in every state: the ledger card is
       * keyed on its client, and a class on it would replay the fade on every pick. */}
      <div
        className={`${String(frame.layout)} ${content.kind === 'ready' ? gate.reveal : ''}`}
        aria-busy={gate.waiting}
      >
        {content.kind === 'waiting' ? (
          // Nothing stands in the wait: both cards are as tall as their lists, which only the
          // answer knows, so each is drawn once, whole, where it stays.
          <span className={hidden.srOnly}>{t('web:booking.credits.loading')}</span>
        ) : content.kind === 'failed' ? (
          <div
            className={`${String(frame.card)} ${String(frame.failed)} ${gate.reveal}`}
            role="alert"
          >
            <p className={frame.failedTitle}>
              {t('web:booking.trainer.credits.clients.errorTitle')}
            </p>
            <button type="button" className={creditActClass} onClick={tab.retry}>
              {t('web:booking.trainer.credits.retry')}
            </button>
          </div>
        ) : content.kind === 'off' ? (
          <div className={`${String(styles.off)} ${gate.reveal}`}>
            <EmptyState
              mark={<CreditMarkIcon />}
              tone="neutral"
              title={t('web:booking.trainer.credits.offTitle')}
              hint={t('web:booking.credits.off.body')}
            />
            <p className={styles.offNote}>
              {t('web:booking.trainer.settings.rows.creditsEnabled.effect')}
            </p>
            <button
              type="button"
              className={`${String(ember.ember)} ${String(styles.offCta)}`}
              aria-busy={tab.switching}
              // While the last switch is still being read back the act is not taken — and says so,
              // rather than swallowing the press.
              aria-disabled={tab.switching ? true : undefined}
              onClick={() => {
                tab.switchTo(true);
              }}
            >
              {t('web:booking.credits.off.cta')}
            </button>
          </div>
        ) : content.rows.length === 0 ? (
          <div className={`${String(styles.off)} ${gate.reveal}`}>
            <EmptyState
              mark={<InboxMarkIcon />}
              tone="neutral"
              title={t('web:booking.trainer.credits.clients.emptyTitle')}
              hint={t('web:booking.trainer.credits.clients.emptyBody')}
            />
          </div>
        ) : (
          <>
            <BalancesCard
              rows={content.rows}
              openId={content.open?.customerId ?? null}
              since={
                content.since === null
                  ? null
                  : t('web:booking.credits.list.since', {
                      date: proseDate(content.since, tab.zone, i18n.language),
                    })
              }
              nameOf={nameOf}
              onOpen={(customerId) => {
                tab.open(customerId);
                onOpenClient(customerId);
              }}
            />
            {content.open === null ? null : (
              <LedgerCard
                key={content.open.customerId}
                row={content.open}
                name={nameOf(content.open)}
                context={context}
                announce={tab.announce}
              />
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        prompt={
          tab.askingOff
            ? {
                title: t('web:booking.trainer.settings.creditsOff.title'),
                description: t('web:booking.trainer.settings.creditsOff.body'),
                confirmLabel: t('web:booking.trainer.settings.creditsOff.confirm'),
                cancelLabel: t('web:booking.trainer.settings.creditsOff.cancel'),
              }
            : null
        }
        onConfirm={tab.confirmOff}
        onCancel={tab.keepOn}
      />
      <LiveRegionAnnouncer message={tab.announcement} />
    </div>
  );
}
